import { Component, computed, effect, ElementRef, input, model, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStateRulesComponent } from "./workflow.state.rules.component";
import { S25Util } from "../../util/s25-util";
import { ExpandTrigger, WorkflowSteps } from "./workflow.process.types";
import { WorkflowUtil } from "./workflow.util";

@TypeManagerDecorator("workflow-state-rules-step")
@Component({
    selector: "workflow-state-rules-step",
    template: `
        <div class="state-rules-step-container">
            <div class="step-header">
                <h3>Approval Rules</h3>
                <p class="step-description">
                    Configure rules for each approval to control when they are enabled or disabled. Only approvals that
                    already have rules are listed below; use the dropdown to choose an approval and add a rule.
                </p>
            </div>

            @if (rulePresenceLoading()) {
                <p class="loading-hint">Loading approval rules…</p>
            }

            @if (!workflowProcess().workflowProcessId) {
                <div class="rules-unavailable">
                    <p>Approval rules will be available after saving the workflow process.</p>
                </div>
            } @else if (!eligibleStatesForRules().length) {
                <div class="no-states">
                    <p>No approvals defined. Please add and save approvals in the previous step.</p>
                </div>
            } @else {
                <div class="add-rule-section">
                    <label for="add-state-select">Select an approval to add a rule</label>
                    <select
                        id="add-state-select"
                        class="state-picker"
                        [ngModel]="addStateSelectValue()"
                        (ngModelChange)="onAddStateSelectChange($event)"
                    >
                        <option value="">Choose an approval…</option>
                        @for (state of eligibleStatesForRules(); track state.workflowStateId) {
                            <option [value]="state.workflowStateId">
                                {{ state.stateName }} ({{ getStageForState(state)?.stageName }})
                            </option>
                        }
                    </select>
                    @if (selectedStateForAdd()) {
                        <div class="add-state-editor">
                            <div class="add-state-editor-toolbar">
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline"
                                    (click)="cancelAddState()"
                                >
                                    Cancel
                                </button>
                            </div>
                            <workflow-state-rules
                                [selectedState]="selectedStateForAdd()"
                                [workflowProcessId]="workflowProcess().workflowProcessId"
                                (rulesChanged)="onStateRulesMutated()"
                            ></workflow-state-rules>
                        </div>
                    }
                </div>

                <div class="states-list">
                    @for (state of statesWithRules(); track state.workflowStateId) {
                        <div class="state-item" [attr.data-state-id]="state.workflowStateId">
                            <div class="state-header" (click)="toggleStateExpansion(state)">
                                <div class="state-info">
                                    <span class="state-name">{{ state.stateName }}</span>
                                    <span class="stage-context">in {{ getStageForState(state)?.stageName }}</span>
                                </div>
                                <div class="state-actions">
                                    <span class="expand-icon" [class.expanded]="isStateExpanded(state)">
                                        {{ isStateExpanded(state) ? "▼" : "▶" }}
                                    </span>
                                </div>
                            </div>

                            @if (isStateExpanded(state)) {
                                <div class="state-content">
                                    <workflow-state-rules
                                        [selectedState]="state"
                                        [workflowProcessId]="workflowProcess().workflowProcessId"
                                        (rulesChanged)="onStateRulesMutated()"
                                    ></workflow-state-rules>
                                </div>
                            }
                        </div>
                    }
                </div>

                @if (!rulePresenceLoading() && !statesWithRules().length && !selectedStateForAdd()) {
                    <div class="no-states">
                        <p>No approval rules configured yet. Choose an approval above to add a rule.</p>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .state-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .step-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .loading-hint {
            color: var(--color--text-muted);
            margin-bottom: 16px;
        }

        .add-rule-section {
            margin-bottom: 24px;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .add-rule-section label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .add-state-editor {
            margin-top: 16px;
        }

        .add-state-editor-toolbar {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
        }

        .states-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .state-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .state-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .state-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .state-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .state-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .stage-context {
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .expand-icon {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .state-content {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border-radius: 6px;
        }

        .state-picker {
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
            min-width: 240px;
        }

        .no-states {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [WorkflowStateRulesComponent, FormsModule],
})
export class WorkflowStateRulesStepComponent {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    selectedStateTrigger = model<ExpandTrigger>();

    private expandedStates = signal<Set<number | string>>(new Set());
    statesWithRules = signal<WorkflowTypes.WorkflowState[]>([]);
    rulePresenceLoading = signal(false);
    selectedStateForAdd = signal<WorkflowTypes.WorkflowState | null>(null);
    addStateSelectValue = signal<string>("");

    readonly stateIdsWithRules = computed(() => {
        const ids = new Set<number>();
        for (const state of this.statesWithRules()) {
            if (S25Util.isDefined(state.workflowStateId)) ids.add(state.workflowStateId!);
        }
        return ids;
    });

    readonly eligibleStatesForRules = computed(() => {
        const workflow = this.workflowProcess();
        if (!workflow.workflowProcessId) return [];
        const states: WorkflowTypes.WorkflowState[] = [];
        for (const stage of workflow.stages || []) {
            for (const state of stage.states || []) {
                if (state.workflowStateId) states.push(state);
            }
        }
        return states;
    });

    constructor(private el: ElementRef) {
        effect(() => {
            const expandTrigger = this.selectedStateTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.States && S25Util.isDefined(expandTrigger.id)) {
                this.expandStateById(expandTrigger.id);
                this.selectedStateTrigger.set(undefined);
            }
        });
        effect(() => {
            this.workflowProcess();
            void this.refreshStatesWithRules();
        });
        effect(() => {
            const stateId = this.selectedStateForAdd()?.workflowStateId;
            if (!S25Util.isDefined(stateId)) return;
            if (this.stateIdsWithRules().has(stateId!)) {
                this.selectedStateForAdd.set(null);
                this.addStateSelectValue.set("");
            }
        });
    }

    async refreshStatesWithRules() {
        this.rulePresenceLoading.set(true);
        try {
            const states = await WorkflowUtil.loadStatesWithWorkflowRules(this.workflowProcess());
            this.statesWithRules.set(states);
        } finally {
            this.rulePresenceLoading.set(false);
        }
    }

    onStateRulesMutated() {
        void this.refreshStatesWithRules();
    }

    cancelAddState() {
        this.selectedStateForAdd.set(null);
        this.addStateSelectValue.set("");
        void this.refreshStatesWithRules();
    }

    onAddStateSelectChange(raw: string) {
        this.addStateSelectValue.set(raw);
        if (!raw) {
            this.selectedStateForAdd.set(null);
            return;
        }
        const stateId = Number(raw);
        const state = this.eligibleStatesForRules().find((candidate) => candidate.workflowStateId === stateId);
        if (!state) {
            this.selectedStateForAdd.set(null);
            return;
        }
        if (this.stateIdsWithRules().has(stateId)) {
            this.selectedStateForAdd.set(null);
            this.expandStateById(stateId);
            return;
        }
        for (const listed of this.statesWithRules()) {
            if (this.isStateExpanded(listed)) {
                this.toggleStateExpansion(listed);
            }
        }
        this.selectedStateForAdd.set(state);
    }

    getStageForState(state: WorkflowTypes.WorkflowState): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.states?.some(
                    (stageState) =>
                        (stageState.workflowStateId && stageState.workflowStateId === state.workflowStateId) ||
                        (stageState.id && stageState.id === state.id),
                ),
            ) || null
        );
    }

    toggleStateExpansion(state: WorkflowTypes.WorkflowState) {
        const stateKey = state.workflowStateId || state.id!;
        const expanded = this.expandedStates();

        if (expanded.has(stateKey)) {
            expanded.delete(stateKey);
        } else {
            expanded.add(stateKey);
        }

        this.expandedStates.set(new Set(expanded));
    }

    isStateExpanded(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.expandedStates().has(stateKey);
    }

    expandStateById(stateId: number) {
        const state = this.statesWithRules().find((candidate) => candidate.workflowStateId === stateId);
        if (!state) return;

        const stateKey = state.workflowStateId ?? state.id!;
        const expanded = this.expandedStates();
        if (!expanded.has(stateKey)) {
            expanded.add(stateKey);
            this.expandedStates.set(new Set(expanded));
        }
        setTimeout(() => {
            const stateEl = this.el.nativeElement.querySelector(`[data-state-id="${stateKey}"]`);
            stateEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }
}
